import type { CheckoutSession, PaymentOption } from '@/types';
import type { MoneyAmount } from '@/shared/types/payment';
import {
  CREDIT_DECLINE_ACTION_TYPE,
  CREDIT_DECLINE_BREAKDOWN_TYPE,
  type CreditDeclineAction,
  type CreditDeclineActionType,
  type CreditDeclineBreakdownItem,
  type CreditDeclineBreakdownType,
  type CreditDeclineInfo,
} from '@/shared/types/credit-decline';

/** Flip to `true` to hide the external obligations breakdown row. */
export const HIDE_DECLINE_EXTERNAL_OBLIGATIONS = false;

export function getVisibleDeclineBreakdown(
  breakdown: CreditDeclineBreakdownItem[],
): CreditDeclineBreakdownItem[] {
  if (!HIDE_DECLINE_EXTERNAL_OBLIGATIONS) {
    return breakdown;
  }

  return breakdown.filter(
    (item) => item.type !== CREDIT_DECLINE_BREAKDOWN_TYPE.EXTERNAL_OBLIGATIONS,
  );
}

const AFFORDABILITY_DECLINE_CODES = new Set([
  '900013',
  '900027',
  '900146',
  '900147',
]);

const DBR_DECLINE_CODES = new Set(['900012', '900026']);

export function isPayInFullOnlyAvailable(
  paymentOptions: PaymentOption[],
): boolean {
  return paymentOptions.length === 1 && paymentOptions[0]?.tenure === 0;
}

export function getPayInFullOption(
  paymentOptions: PaymentOption[],
): PaymentOption | undefined {
  return paymentOptions.find((option) => option.tenure === 0);
}

export function isZeroRecommendedLimit(
  recommendedLimit: MoneyAmount | null,
): boolean {
  return recommendedLimit != null && recommendedLimit.amount === 0;
}

export function getDeclineHeadlineKey(
  declineCode: string,
): 'affordability' | 'dbr' | 'generic' {
  if (AFFORDABILITY_DECLINE_CODES.has(declineCode)) {
    return 'affordability';
  }
  if (DBR_DECLINE_CODES.has(declineCode)) {
    return 'dbr';
  }
  return 'generic';
}

export function getDeclineSubtitleKey(
  hasZeroRecommendedLimit: boolean,
): 'zero_limit' | 'affordability' {
  return hasZeroRecommendedLimit ? 'zero_limit' : 'affordability';
}

/**
 * Which breakdown row each action decorates (chevron + tappable sub-heading).
 * - LEANTECH_CONNECTION → INCOME ("Link your bank account…")
 * - PAY_DUES → INTERNAL_OBLIGATIONS ("Pay outstanding payments…")
 * - QUESTIONNAIRE → EXPENSES ("Review the information used…")
 */
const ACTION_TYPE_TO_BREAKDOWN_TYPE: Record<
  CreditDeclineActionType,
  CreditDeclineBreakdownType
> = {
  [CREDIT_DECLINE_ACTION_TYPE.LEANTECH_CONNECTION]:
    CREDIT_DECLINE_BREAKDOWN_TYPE.INCOME,
  [CREDIT_DECLINE_ACTION_TYPE.PAY_DUES]:
    CREDIT_DECLINE_BREAKDOWN_TYPE.INTERNAL_OBLIGATIONS,
  [CREDIT_DECLINE_ACTION_TYPE.QUESTIONNAIRE]:
    CREDIT_DECLINE_BREAKDOWN_TYPE.EXPENSES,
};

/** Returns the action that decorates a given breakdown row, if any. */
export function getActionForBreakdownType(
  actions: CreditDeclineAction[] | undefined,
  breakdownType: CreditDeclineBreakdownType,
): CreditDeclineAction | undefined {
  if (!actions || actions.length === 0) {
    return undefined;
  }
  return actions.find(
    (action) => ACTION_TYPE_TO_BREAKDOWN_TYPE[action.type] === breakdownType,
  );
}

export function getBreakdownLabelKey(type: CreditDeclineBreakdownType): string {
  switch (type) {
    case CREDIT_DECLINE_BREAKDOWN_TYPE.INCOME:
      return 'decline_ui.breakdown.income.label';
    case CREDIT_DECLINE_BREAKDOWN_TYPE.EXTERNAL_OBLIGATIONS:
      return 'decline_ui.breakdown.external_obligations.label';
    case CREDIT_DECLINE_BREAKDOWN_TYPE.INTERNAL_OBLIGATIONS:
      return 'decline_ui.breakdown.internal_obligations.label';
    case CREDIT_DECLINE_BREAKDOWN_TYPE.EXPENSES:
      return 'decline_ui.breakdown.expenses.label';
    default:
      return 'decline_ui.breakdown.income.label';
  }
}

export function getBreakdownDescriptionKey(
  type: CreditDeclineBreakdownType,
): string {
  switch (type) {
    case CREDIT_DECLINE_BREAKDOWN_TYPE.INCOME:
      return 'decline_ui.breakdown.income.description';
    case CREDIT_DECLINE_BREAKDOWN_TYPE.EXTERNAL_OBLIGATIONS:
      return 'decline_ui.breakdown.external_obligations.description';
    case CREDIT_DECLINE_BREAKDOWN_TYPE.INTERNAL_OBLIGATIONS:
      return 'decline_ui.breakdown.internal_obligations.description';
    case CREDIT_DECLINE_BREAKDOWN_TYPE.EXPENSES:
      return 'decline_ui.breakdown.expenses.description';
    default:
      return 'decline_ui.breakdown.income.description';
  }
}

export function buildDeclineUiAnalyticsPayload(
  session: CheckoutSession,
  creditDeclineInfo: CreditDeclineInfo,
  showPayInFull: boolean,
  shownActionTypes: string[] = [],
) {
  return {
    decline_reason: creditDeclineInfo.declineCode,
    order_amount: creditDeclineInfo.orderAmount?.amount ?? null,
    recommended_limit: creditDeclineInfo.recommendedLimit?.amount ?? null,
    breakdown_shown: getVisibleDeclineBreakdown(creditDeclineInfo.breakdown).map(
      (item) => item.type,
    ),
    pay_in_full_shown: showPayInFull,
    actions_shown: shownActionTypes,
    checkout_id: session.id,
    order_id: session.order?.id ?? '',
  };
}
